import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LinkComponent } from '@vg-constellation/angular-19/link';
import { NavigationComponent } from '@vg-constellation/angular-19/navigation';
import { NavbarComponent } from './components/navbar/navbar.component';
import { StatementMessagingComponent } from './components/statement-messaging/statement-messaging.component';
import { InternalBannerComponent } from './components/internal-banner/internal-banner.component';
import { InternalCalloutComponent } from '@vg-constellation/angular-19/internal-callout';
import { TitleComponent } from './components/title/title.component';
import { ButtonComponent } from '@vg-constellation/angular-19/button';
import { NavOverflowComponent, NavOverflowModule } from '@vg-constellation/angular-19/nav-overflow';
import { IconComponent } from '@vg-constellation/angular-19/icon';
import {
  RadioComponent,
  RadioGroupComponent,
  RadioModule,
} from '@vg-constellation/angular-19/radio';
import {
  DataTableBodyCellComponent,
  DataTableBodyRowComponent,
  DataTableCaptionComponent,
  DataTableComponent,
  DataTableHeaderCellComponent,
  DataTableHeaderRowComponent,
  DataTableModule,
} from '@vg-constellation/patterns-angular/data-table';
import { BannerComponent, BannerModule } from '@vg-constellation/angular-19/banner';
import {
  HTTP_INTERCEPTORS,
  HttpContextToken,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from '@angular/common/http';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { PaginationComponent } from '@vg-constellation/angular-19/pagination';
import { SpinnerComponent } from '@vg-constellation/angular-19/spinner';
import {
  ModalDialogComponent,
  ModalDialogEntrypointComponent,
  ModalDialogModule,
} from '@vg-constellation/angular-19/modal-dialog';
import { SelectComponent, SelectModule } from '@vg-constellation/angular-19/select';
import { HintErrorComponent } from '@vg-constellation/angular-19/hint-error';
import { DatePickerComponent, DatePickerModule } from '@vg-constellation/angular-19/date-picker';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputModule } from '@vg-constellation/angular-19/input';
import { LoginErrorComponent } from './components/login-error/login-error.component';
import { AppSpinnerComponent } from './components/app-spinner/app-spinner.component';
import { FilterUtil } from './core/utility/filter.util';
import { MessageDetailActionComponent } from './components/message-detail-action/message-detail-action.component';
import { MessageRunDetailNavigationOverflowMenuComponent } from './components/message-detail-action/message-run-detail-navigation-overflow-menu/message-run-detail-navigation-overflow-menu.component';
import { MessageDetailSearchModalComponent } from './components/message-detail-action/message-detail-search-modal/message-detail-search-modal.component';
import { LabelComponent } from '@vg-constellation/angular-19/label';
import { MessageDetailTableComponent } from './components/message-detail-action/message-detail-table/message-detail-table.component';
import { YesNoPipe } from './pipes/yes-no/yes-no.pipe';
import { MessageAddComponent } from './components/message-add/message-add.component';
import {
  TextareaComponent,
  TextareaTextareaComponent,
} from '@vg-constellation/angular-19/textarea';
import { CheckboxComponent, CheckboxInputComponent } from '@vg-constellation/angular-19/checkbox';
import { ViewUpdateMessageComponent } from './components/view-update-message/view-update-message.component';
import { ViewUpdateMessageNavigationOverflowMenuComponent } from './components/view-update-message/view-update-message-navigation-overflow-menu/view-update-message-navigation-overflow-menu.component';
import { ConfirmSubmitComponent } from './components/confirm-submit/confirm-submit.component';
import { XsrfInterceptor } from './core/interceptors/xsrf-token.interceptor';
import { PreviewMessageComponent } from './components/preview-message/preview-message.component';
import { FrequencyLabel } from './pipes/frequency-label/frequency-label.pipe';
import { HasPermissionDirective } from './core/directives/has-permission/has-permission.directive';
import { LimitedCharsPipe } from './pipes/limited-chars/limited-chars.pipe';
import { FundIdNameFormationPipe } from './pipes/fundid-name/fund-id-name-formation.pipe';

export const BYPASS_AUTH_INTERCEPTOR = new HttpContextToken<boolean>(() => false);
export const BYPASS_XSRF_AUTH_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

const oktaAuth = new OktaAuth(environment.oidc);

@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    StatementMessagingComponent,
    InternalBannerComponent,
    TitleComponent,
    LoginErrorComponent,
    AppSpinnerComponent,
    MessageDetailActionComponent,
    MessageRunDetailNavigationOverflowMenuComponent,
    MessageDetailSearchModalComponent,
    MessageDetailTableComponent,
    MessageAddComponent,
    ViewUpdateMessageComponent,
    ViewUpdateMessageNavigationOverflowMenuComponent,
    ConfirmSubmitComponent,
    PreviewMessageComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LinkComponent,
    NavigationComponent,
    InternalCalloutComponent,
    ButtonComponent,
    NavOverflowComponent,
    NavOverflowModule,
    IconComponent,
    RadioComponent,
    DataTableModule,
    DataTableComponent,
    DataTableHeaderRowComponent,
    DataTableBodyRowComponent,
    DataTableHeaderCellComponent,
    DataTableBodyCellComponent,
    DataTableCaptionComponent,
    PaginationComponent,
    BannerComponent,
    RadioGroupComponent,
    OktaAuthModule,
    SpinnerComponent,
    ModalDialogComponent,
    ModalDialogEntrypointComponent,
    SelectComponent,
    HintErrorComponent,
    DatePickerComponent,
    FormsModule,
    ReactiveFormsModule,
    InputModule,
    ModalDialogModule,
    SelectModule,
    DatePickerModule,
    RadioModule,
    BannerModule,
    LabelComponent,
    YesNoPipe,
    TextareaComponent,
    CheckboxComponent,
    TextareaTextareaComponent,
    CheckboxInputComponent,
    FrequencyLabel,
    HasPermissionDirective,
    LimitedCharsPipe,
    FundIdNameFormationPipe,
  ],
  providers: [
    Title,
    // Use provideHttpClient instead
    provideHttpClient(
      withInterceptorsFromDi(), // If you need interceptors
      // withFetch(), // Optional: Use fetch API
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-XSRF-TOKEN',
      }),
    ),
    // Okta configuration
    { provide: OKTA_CONFIG, useValue: { oktaAuth } },
    // Register interceptors in order - XsrfInterceptor first, then AuthInterceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XsrfInterceptor,
      multi: true,
    },
    // Register interceptors in order
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    FilterUtil,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [TitleComponent],
})
export class AppModule {}
